<script setup lang="ts">
import { useAnalytics } from '@/composables/analytics';
import { useNewRelic } from '@/composables/useNewRelic';
import { useNotification } from '@/composables/useNotification';
import CatalogPdf from '@/features/generate-catalog-pdf/components/CatalogPdf.vue';
import { useCatalogPdfGeneration } from '@/features/generate-catalog-pdf/composables/useCatalogPdfGeneration';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { useUserStore } from '@/stores/user';
import { SelfSellInCatalogDownloadType } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDraftsInitInfoStore } from '../../../../stores/drafts-init-info';
import type { ResolvedSelfSellInModel } from '../../composables/useSelfSellIn';
import SectionContainer from '../SectionContainer.vue';
import SectionHeader from '../SectionHeader.vue';
import CatalogCard from './CatalogCard.vue';
import PricatSection from './PricatSection.vue';

defineProps<{
  data: NonNullable<ResolvedSelfSellInModel['catalogDownloadSection']>;
}>();

const { t } = useI18n();
const { notifyWarning } = useNotification();
const newRelic = useNewRelic();
const { addCustomGaEvent } = useAnalytics();
const printContainerRef = ref<HTMLElement | null>(null);
const { generateCatalogPdf, generatingPdf, catalogData } = useCatalogPdfGeneration(printContainerRef);
const { currentSeason } = storeToRefs(useDraftsInitInfoStore());
const { selectedAccount } = storeToRefs(useUserStore());

const handleDownload = async (
  catalog: NonNullable<ResolvedSelfSellInModel['catalogDownloadSection']>['catalogs'][number],
) => {
  if (!currentSeason.value) {
    notifyWarning(t('viewSelfSellIn.catalog_download.season_missing'));

    return;
  }

  try {
    if (catalog.type === SelfSellInCatalogDownloadType.OVERVIEW) {
      if (!catalog.downloadUrl) {
        notifyWarning(t('viewSelfSellIn.catalog_download.missing_url'));

        return;
      }

      window.open(catalog.downloadUrl, '_blank', 'noopener,noreferrer');
    } else {
      await generateCatalogPdf(currentSeason.value);
    }
  } catch (error: any) {
    newRelic.noticeError(error, {
      component: 'CatalogDownloadSection',
      method: 'handleDownload',
      catalogType: catalog.type,
    });

    notifyWarning(
      t(error?.message) || t('viewSelfSellIn.catalog_download.download_error', { fileName: catalog.title }),
    );
  }

  addCustomGaEvent({
    event:
      catalog.type === SelfSellInCatalogDownloadType.SEASON
        ? GaCustomEvents.SelfSellInCatalogPdfDownload
        : GaCustomEvents.SelfSellInCommercialDeckDownload,
    link_text: `File download for ${catalog.type}`,
    file_name: catalog.title,
    component: 'CatalogDownloadSection',
    account_id: selectedAccount.value.id,
    account_name: selectedAccount.value.name,
    account_country: selectedAccount.value.billingCountry,
  });
};
</script>

<template>
  <SectionContainer :section-title="data.title">
    <SectionHeader :title="data.title" :description="data.description" />
    <div class="catalogs">
      <CatalogCard
        v-for="catalog in data.catalogs"
        :key="catalog.title"
        :data="catalog"
        :loading="catalog.type === SelfSellInCatalogDownloadType.SEASON && generatingPdf"
        :id="`download-${catalog.type}`"
        @download="handleDownload" />
    </div>
    <PricatSection :season="data.season" />
  </SectionContainer>
  <!--
    Teleported to <body> so it becomes a direct body child that the global print CSS
    can target with `body > *:not(#catalog-pdf-print-root)`. Staying in the Vue tree
    means it inherits i18n and stores normally.
  -->
  <Teleport to="body">
    <div ref="printContainerRef" id="catalog-pdf-print-root">
      <CatalogPdf v-if="catalogData" :data="catalogData" />
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.catalogs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(265px, 100%), 1fr));
  gap: 4px;

  @include respond-to('tablet') {
    grid-template-columns: repeat(auto-fit, minmax(30%, 1fr));
  }
}
</style>
